import { Component, computed, effect, inject, input, OnInit, output } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { FloorplanFreeText } from "@domain/project/floorplan/floorplan-free-text";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { FloorplanSidesheetExtent } from "@domain/project/floorplan/floorplan-sidesheet-extent";
import { FloorplanFreeTextListType, FREE_TEXT_MAX_FONT_SIZE, FREE_TEXT_MIN_FONT_SIZE } from "@lib/constants/floorplan-free-text";
import { ModalService } from "@odx/angular/components/modal";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";

@Component({
  selector: "app-free-text-notes",
  imports: [UiKitModule, ReactiveFormsModule, FloorplanItemDetailsComponent],
  templateUrl: "./free-text-notes.component.html",
  styleUrl: "./free-text-notes.component.scss",
})
export class FreeTextNotesComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly floorplanService = inject(FloorplanService);
  private modalService = inject(ModalService);

  readonly extent = input.required<FloorplanSidesheetExtent>();
  readonly floorplanItem = input.required<FloorplanItem>();
  readonly toggleExtendedButtonClicked = output<void>();

  protected readonly freeText = computed(() => this.floorplanItem() as FloorplanFreeText);
  protected freeTextForm?: FormGroup;
  protected readonly minFontSize = FREE_TEXT_MIN_FONT_SIZE;
  protected readonly maxFontSize = FREE_TEXT_MAX_FONT_SIZE;
  protected readonly freeTextListType = FloorplanFreeTextListType;
  protected readonly tooltipTextColor = $localize`:@@floorplan.details.freeText.textColor:Textfarbe`;
  protected readonly tooltipTextBold = $localize`:@@floorplan.details.freeText.bold:Fett`;
  protected readonly tooltipTextItalic = $localize`:@@floorplan.details.freeText.italic:Kursiv`;
  protected readonly tooltipTextBulletList = $localize`:@@floorplan.details.freeText.bulletList:Aufzählungsliste`;
  protected readonly tooltipTextNumberedList = $localize`:@@floorplan.details.freeText.numberedList:Nummerierte Liste`;
  protected readonly tooltipTextFontSize = $localize`:@@floorplan.details.freeText.fontSize.label:Schriftgröße`;

  constructor() {
    effect((onCleanup) => {
      const freeText = this.freeText();

      this.freeTextForm?.patchValue(
        {
          content: freeText.content,
          textColor: freeText.textColor,
          bold: freeText.bold,
          italic: freeText.italic,
          fontSize: freeText.fontSize,
          listType: freeText.listType,
        },
        { emitEvent: false },
      );

      const fontSizeSubscription = freeText.fontSizeChanged$.subscribe((fontSize) =>
        this.freeTextForm?.get("fontSize")?.patchValue(fontSize, { emitEvent: false }),
      );
      onCleanup(() => fontSizeSubscription.unsubscribe());
    });
  }

  ngOnInit(): void {
    this.freeTextForm = this.formBuilder.group({
      content: [this.freeText().content, Validators.maxLength(2000)],
      textColor: [this.freeText().textColor],
      bold: [this.freeText().bold],
      italic: [this.freeText().italic],
      fontSize: [this.freeText().fontSize, [Validators.min(this.minFontSize), Validators.max(this.maxFontSize)]],
      listType: [this.freeText().listType],
    });

    this.freeTextForm.valueChanges.subscribe(() => {
      if (!this.freeTextForm!.valid || this.freeTextForm!.pristine) return;

      const content = this.freeTextForm!.value.content ?? "";
      if (content.trim().length === 0) {
        return;
      }

      this.freeText().content = content;
    });

    this.freeTextForm.get("textColor")?.valueChanges.subscribe((textColor) => {
      if (!textColor || this.freeText().locked) {
        return;
      }
      this.freeText().textColor = textColor;
    });

    this.freeTextForm.get("bold")?.valueChanges.subscribe((bold: boolean | null) => {
      if (bold === null || this.freeText().locked) {
        return;
      }
      this.freeText().bold = bold;
    });

    this.freeTextForm.get("italic")?.valueChanges.subscribe((italic: boolean | null) => {
      if (italic === null || this.freeText().locked) {
        return;
      }
      this.freeText().italic = italic;
    });

    this.freeTextForm.get("fontSize")?.valueChanges.subscribe((fontSize: number | null) => {
      if (
        fontSize === null ||
        !Number.isInteger(fontSize) ||
        fontSize < this.minFontSize ||
        fontSize > this.maxFontSize ||
        this.freeText().locked
      ) {
        return;
      }

      this.freeText().fontSize = fontSize;
    });

    this.freeTextForm.get("listType")?.valueChanges.subscribe((listType: FloorplanFreeTextListType | null) => {
      if (!listType || this.freeText().locked) {
        return;
      }

      this.freeText().listType = listType;
    });
  }

  protected onContentBlur(): void {
    if (this.freeText().locked) {
      return;
    }

    const content = this.freeTextForm?.get("content")?.value;
    if (typeof content === "string" && content.trim().length === 0) {
      this.modalService
        .open(ConfirmModalComponent, {
          data: {
            headerText: $localize`:@@floorplan.freeTexts.modal.header:Kein Text`,
            text: $localize`:@@floorplan.freeTexts.modal.confirmReset:Freitexte ohne Inhalt werden gelöscht. Fortfahren?`,
            confirmButtonText: $localize`:@@floorplan.freeTexts.modal.confirm:Bestätigen`,
          },
        })
        .onClose$.subscribe((result) => {
          if (result !== "confirmed") {
            return;
          }
          this.floorplanService.deleteSelectedItem();
        });
    }
  }

  protected toggleBold(): void {
    const control = this.freeTextForm?.get("bold");
    control?.setValue(!control.value);
  }

  protected toggleItalic(): void {
    const control = this.freeTextForm?.get("italic");
    control?.setValue(!control.value);
  }

  protected toggleBulletList(): void {
    const control = this.freeTextForm?.get("listType");
    control?.setValue(
      control.value === FloorplanFreeTextListType.BULLET ? FloorplanFreeTextListType.NONE : FloorplanFreeTextListType.BULLET,
    );
  }

  protected toggleNumberedList(): void {
    const control = this.freeTextForm?.get("listType");
    control?.setValue(
      control.value === FloorplanFreeTextListType.NUMBERED ? FloorplanFreeTextListType.NONE : FloorplanFreeTextListType.NUMBERED,
    );
  }
}
